import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

/**
 * `system` is the default, and it is a third value rather than a boolean plus a
 * flag: an operator who has never touched the switch should follow the OS, and
 * one who has chosen dark should *stay* dark when the OS flips at sunset. Those
 * are three states, so the store holds three.
 */
export type ThemePreference = 'light' | 'dark' | 'system';

const THEME_KEY = 'venue.theme';

export const THEME_PREFERENCES: readonly ThemePreference[] = ['light', 'dark', 'system'];

function storedTheme(): ThemePreference {
  const raw = localStorage.getItem(THEME_KEY);
  // Anything unrecognised — including the key's absence — means "not chosen".
  return THEME_PREFERENCES.includes(raw as ThemePreference)
    ? (raw as ThemePreference)
    : 'system';
}

/** What `system` currently resolves to. Safe on a server/jsdom with no matchMedia. */
export function systemPrefersDark(): boolean {
  return typeof window !== 'undefined' && typeof window.matchMedia === 'function'
    ? window.matchMedia('(prefers-color-scheme: dark)').matches
    : false;
}

export function resolveTheme(preference: ThemePreference): 'light' | 'dark' {
  if (preference === 'system') return systemPrefersDark() ? 'dark' : 'light';
  return preference;
}

interface UiState {
  sidebarOpen: boolean;
  /**
   * The rail as an overlay, which is what it has to be below `md`: at 240px it
   * is three quarters of a 320pt phone, and it used to be `shrink-0` at every
   * width with no way to get it out of the way.
   */
  mobileNavOpen: boolean;
  theme: ThemePreference;
  activeBranchId: string | null;
}

const initialState: UiState = {
  sidebarOpen: true,
  mobileNavOpen: false,
  theme: storedTheme(),
  activeBranchId: localStorage.getItem('venue.branch'),
};

const uiSlice = createSlice({
  name: 'ui',
  initialState,
  reducers: {
    toggleSidebar(state) {
      state.sidebarOpen = !state.sidebarOpen;
    },
    toggleMobileNav(state) {
      state.mobileNavOpen = !state.mobileNavOpen;
    },
    /** Following a link must take the overlay down with it. */
    closeMobileNav(state) {
      state.mobileNavOpen = false;
    },
    /**
     * Persistence only. The `dark` class is applied by App.tsx, which is also
     * what has to re-run when the OS preference changes under `system` — a
     * reducer cannot subscribe to a media query, and having two places toggle
     * the same class is how one of them ends up wrong.
     */
    setTheme(state, action: PayloadAction<ThemePreference>) {
      state.theme = action.payload;
      localStorage.setItem(THEME_KEY, action.payload);
    },
    setActiveBranch(state, action: PayloadAction<string | null>) {
      state.activeBranchId = action.payload;
      if (action.payload) localStorage.setItem('venue.branch', action.payload);
      else localStorage.removeItem('venue.branch');
    },
  },
});

export const { toggleSidebar, toggleMobileNav, closeMobileNav, setTheme, setActiveBranch } =
  uiSlice.actions;
export default uiSlice.reducer;
